import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BellRinging, DeviceMobile, DownloadSimple, Export, X } from '@phosphor-icons/react';
import type { SessionUser } from '@helpdesk/shared';
import { getJson } from '../api/client.ts';
import { installAvailable, isStandalone, onInstallAvailabilityChange, promptInstall } from '../pwa.ts';
import { PushNotReadyError, pushState, subscribePush, type PushState } from '../lib/push.ts';
import { Button } from './ui.tsx';

/**
 * Ein Hinweis oben in der App – immer nur einer, und nur das, was auf diesem
 * Gerät wirklich geht:
 *   1. iPhone oder iPad im Browser → „Zum Home-Bildschirm“ (sonst gibt es dort kein Push)
 *   2. Push möglich, aber aus → „Benachrichtigungen einschalten“
 *   3. Browser bietet Installieren an → „Als App installieren“
 * „Später“ blendet den Hinweis 14 Tage aus. Geteilte Hallen-Tablets fragen nie.
 */
const SNOOZE_DAYS = 14;

function snoozed(key: string): boolean {
  try {
    const at = Number(localStorage.getItem(`hd.prompt.${key}`) ?? 0);
    return Date.now() - at < SNOOZE_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function snooze(key: string): void {
  try {
    localStorage.setItem(`hd.prompt.${key}`, String(Date.now()));
  } catch {
    /* privater Modus: dann eben beim nächsten Mal wieder */
  }
}

export function AppPrompts({ user, vapidPublicKey }: { user: SessionUser; vapidPublicKey: string | null }) {
  const [push, setPush] = useState<PushState | null>(null);
  const [canInstall, setCanInstall] = useState(installAvailable());
  const [hidden, setHidden] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    // Ohne Stand entfällt der Push-Hinweis, der Installationshinweis bleibt. Den Fehler nennt das Profil.
    void pushState(user, vapidPublicKey).then(setPush, () => setPush('unsupported'));
  }, [user, vapidPublicKey]);

  useEffect(() => onInstallAvailabilityChange(() => setCanInstall(installAvailable())), []);

  if (user.deviceId || push === null) return null;

  const dismiss = (key: string) => {
    snooze(key);
    setHidden(key);
  };

  if (push === 'ios-install' && !snoozed('ios') && hidden !== 'ios') {
    return (
      <Banner icon={<DeviceMobile size={26} weight="duotone" />} onClose={() => dismiss('ios')}>
        <p className="font-semibold text-steel-900">Als App auf den Home-Bildschirm legen</p>
        <p className="text-[15px] text-steel-700">
          Tippen Sie auf das Teilen-Symbol <Export size={18} className="inline align-[-3px]" aria-hidden /> und dann auf „Zum Home-Bildschirm“. Nur so bekommen Sie auf dem iPhone oder iPad Push-Nachrichten.
        </p>
      </Banner>
    );
  }

  if (push === 'available' && vapidPublicKey && !snoozed('push') && hidden !== 'push') {
    return (
      <Banner
        icon={<BellRinging size={26} weight="duotone" />}
        onClose={() => dismiss('push')}
        action={
          <Button
            size="sm"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const ok = await subscribePush(user.id, vapidPublicKey);
                setPush(ok ? 'subscribed' : await pushState(user, vapidPublicKey));
                setMessage(ok ? null : 'Benachrichtigungen wurden nicht erlaubt.');
              } catch (err) {
                setMessage(err instanceof PushNotReadyError ? err.message : 'Das hat nicht geklappt. Bitte im Profil erneut versuchen.');
              } finally {
                setBusy(false);
              }
            }}
          >
            Einschalten
          </Button>
        }
      >
        <p className="font-semibold text-steel-900">Benachrichtigungen auf diesem Gerät</p>
        <p className="text-[15px] text-steel-700">
          <PushBenefit user={user} />
        </p>
        {message ? <p className="mt-1 text-[14px] font-medium text-error">{message}</p> : null}
      </Banner>
    );
  }

  if (canInstall && !isStandalone() && !snoozed('install') && hidden !== 'install') {
    return (
      <Banner
        icon={<DownloadSimple size={26} weight="duotone" />}
        onClose={() => dismiss('install')}
        action={
          <Button size="sm" variant="secondary" onClick={() => void promptInstall()}>
            Installieren
          </Button>
        }
      >
        <p className="font-semibold text-steel-900">Als App installieren</p>
        <p className="text-[15px] text-steel-700">Startet vom Home-Bildschirm wie eine normale App, im eigenen Fenster.</p>
      </Banner>
    );
  }

  return null;
}

/**
 * Muss zu `notifyCreated`, `notifyAssignee`, `notifyStatus` und `notifyReporterComment` passen. Neue Meldungen
 * jeder Priorität gehen zuerst ans zuständige Team, nicht ans ganze Werk. „Neue“ gehört in den Text, weil
 * `changePriority` das Team nicht benachrichtigt. Die Sammelmail braucht Adresse und Haken im Profil.
 */
function PushBenefit({ user }: { user: SessionUser }) {
  const worker = user.role === 'technician' || user.role === 'lead';
  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => getJson<{ notifyEmail: boolean }>('/api/profile'),
    enabled: worker && Boolean(user.email),
  });
  if (!worker) return 'Sie erfahren sofort, wenn Ihre Meldung angenommen, abgelehnt oder behoben wird oder die Instandhaltung Ihnen schreibt.';
  const digest = Boolean(user.email) && profile.data?.notifyEmail !== false;
  return `Neue dringende Störungen (P1, P2) Ihres Teams und Zuweisungen sofort aufs Handy, auch wenn die App geschlossen ist. ${digest ? 'Neue P3 und P4 Ihres Teams kommen gesammelt per Mail.' : 'P3 und P4 sehen Sie in der Ticketliste.'}`;
}

function Banner({ icon, children, action, onClose }: { icon: ReactNode; children: ReactNode; action?: ReactNode; onClose: () => void }) {
  return (
    <div className="mb-5 flex items-start gap-3 border border-[var(--border-default)] border-l-4 border-l-arnold-red bg-white py-3 pr-2 pl-4 shadow-[var(--shadow-card)]">
      <span className="mt-0.5 shrink-0 text-arnold-red">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-3 leading-snug sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">{children}</div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Später erinnern"
        title="Später"
        className="-mt-1 inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center text-steel-600 hover:bg-steel-100 hover:text-steel-900"
      >
        <X size={20} />
      </button>
    </div>
  );
}
